interface AudioSystem {
  beep(frequency?: number, duration?: number): void;
  klaxon(): void;
  detonation(): void;
  tick(): void;
}

function createAudioSystem(): AudioSystem {
  let ctx: AudioContext | null = null;

  function getCtx(): AudioContext {
    if (!ctx) {
      ctx = new AudioContext();
    }
    return ctx;
  }

  function playTone(frequency: number, duration: number, type: OscillatorType = 'square', gain: number = 0.1): void {
    try {
      const audioCtx = getCtx();
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(frequency, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(gain, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);

      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      osc.start();
      osc.stop(audioCtx.currentTime + duration);
    } catch {
      // Audio not supported or blocked
    }
  }

  return {
    beep(frequency = 800, duration = 0.1): void {
      playTone(frequency, duration, 'square', 0.05);
    },

    klaxon(): void {
      try {
        const audioCtx = getCtx();
        const osc = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(200, audioCtx.currentTime);
        osc.frequency.linearRampToValueAtTime(400, audioCtx.currentTime + 0.5);
        osc.frequency.linearRampToValueAtTime(200, audioCtx.currentTime + 1.0);

        gainNode.gain.setValueAtTime(0.08, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 1.5);

        osc.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        osc.start();
        osc.stop(audioCtx.currentTime + 1.5);
      } catch {
        // Audio not supported
      }
    },

    detonation(): void {
      try {
        const audioCtx = getCtx();
        const bufferSize = audioCtx.sampleRate * 0.5;
        const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
        const data = buffer.getChannelData(0);

        // White noise with decay for explosion
        for (let i = 0; i < bufferSize; i++) {
          data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.2));
        }

        const source = audioCtx.createBufferSource();
        source.buffer = buffer;

        const gainNode = audioCtx.createGain();
        gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.5);

        const filter = audioCtx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(300, audioCtx.currentTime);

        source.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        source.start();
      } catch {
        // Audio not supported
      }
    },

    tick(): void {
      playTone(1200, 0.02, 'square', 0.03);
    },
  };
}

export { createAudioSystem };
export type { AudioSystem };
